/* eslint-disable react/display-name */
import React, { useCallback, useMemo } from 'react';
import { useTheme, Tooltip, IconInfo } from '@veneer/core';
import {
  useDocumentUrl,
  useFeatureFlagContext
} from '@eu-data-act/react-euda-commons/dist/index';

import {
  LearnMoreButtonContainer,
  ScreenReaderOnly
} from '../device-item/styled';
import { PlainButton } from '../common/styled';

import { useDependencyManagerContext } from '../../contexts/dependencyManager';
import { getFileName } from '../../utils/get-document-name';

type PDFLearnMoreButtonProps = {
  categoryType: string;
  deviceTelemetryFormat: string;
  'data-testid': string;
  tabIndex?: number;
};

const PDFLearnMoreButton: React.FC<PDFLearnMoreButtonProps> = React.memo(
  (props) => {
    const {
      categoryType,
      deviceTelemetryFormat,
      'data-testid': dataTestId,
      tabIndex
    } = props;
    const theme = useTheme();
    const {
      translate,
      northboundAPIs: {
        v1: {
          localization: { country, language }
        }
      }
    } = useDependencyManagerContext();
    const releasePlatformHpAnalytics = useFeatureFlagContext();

    const documentName = getFileName(categoryType, deviceTelemetryFormat);

    const { isDocumentAvailable, documentUrl } = useDocumentUrl({
      documentName,
      country,
      language
    });

    const learnMoreAnalyticsAttributes = useMemo(() => {
      return releasePlatformHpAnalytics
        ? {
            'data-gtm-category': 'document',
            'data-gtm-id': documentName,
            'data-gtm-value': 'PDF',
            'data-gtm-action': `view ${categoryType} ${deviceTelemetryFormat} document`
          }
        : {};
    }, [
      releasePlatformHpAnalytics,
      documentName,
      categoryType,
      deviceTelemetryFormat
    ]);

    const handleLearnMoreClick = useCallback(() => {
      if (documentUrl) {
        window.open(documentUrl, '_blank', 'noopener,noreferrer');
      }
    }, [documentUrl]);

    return (
      <LearnMoreButtonContainer $veneerTheme={theme}>
        <PlainButton
          $veneerTheme={theme}
          appearance="ghost"
          data-testid={dataTestId}
          onClick={handleLearnMoreClick}
          disabled={!isDocumentAvailable}
          tabIndex={tabIndex}
          aria-label={translate(
            'components.device-item.learn-more-button.aria-label',
            { categoryType }
          )}
          aria-describedby={`${dataTestId}-description`}
          {...learnMoreAnalyticsAttributes}
        >
          {translate('components.device-item.learn-more-button.text')}
          <ScreenReaderOnly
            id={`${dataTestId}-description`}
            aria-live="polite"
          >
            {translate(
              'components.device-item.learn-more-button.aria-description',
              { categoryType }
            )}
          </ScreenReaderOnly>
        </PlainButton>
        {!isDocumentAvailable && (
          <Tooltip
            contentHideDelayOnHover={300}
            contentShowDelayOnHover={300}
            placement="top-end"
            content={
              <span>
                {translate(
                  'components.device-item.learn-more-button.unavailable'
                )}
              </span>
            }
          >
            <IconInfo />
          </Tooltip>
        )}
      </LearnMoreButtonContainer>
    );
  }
);

export default PDFLearnMoreButton;
